import { AxiosProgressEvent, AxiosRequestConfig } from 'axios';

import { HttpClient } from '@zeta-core';
import {
  APIResponse,
  IAttachDocumentRequest,
  IAttachDocumentResponse,
  IDocumentDownloadRequest,
  IDocumentEditRequest,
  IDocumentPresignResponse,
  IDocumentSearchRequest,
  IDocumentSearchResponse,
  IDocumentUploadRequest,
  IDocumentUploadResponse,
  IEmptyRequestResponse,
  IGetDeletedDocumentsResponse,
  IGetDocumentTypesRequest,
  IGetDocumentTypesResponse,
} from '@zeta-types';
import { DOCUMENT_SERVICE } from '../services-path';
import { IDocumentCategoryViewResponse, IDocumentStatusViewResponse } from './types';

export const DocumentService = () => {
  return {
    createDocument: (payload: IDocumentUploadRequest, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): APIResponse<IDocumentUploadResponse> =>
      HttpClient.upload(`${DOCUMENT_SERVICE}/document/file/s3/upload`, payload, { onUploadProgress }),

    createBatchDocuments: (
      payload: IDocumentUploadRequest,
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
    ): APIResponse<IDocumentUploadResponse> => HttpClient.upload(`${DOCUMENT_SERVICE}/document/file/s3/batchupload`, payload, { onUploadProgress }),

    updateDocument: (payload: IDocumentUploadRequest): APIResponse<IDocumentUploadResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/file/s3/update`, payload),

    attachDocument: (payload: IAttachDocumentRequest, config: AxiosRequestConfig): APIResponse<IAttachDocumentResponse> =>
      HttpClient.upload(`${DOCUMENT_SERVICE}/document/task/attachment/upload`, payload, config),

    deleteDocument: (payload: IDocumentUploadRequest): APIResponse<IDocumentUploadResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/file/s3/delete`, payload),

    downloadDocument: (payload: IDocumentDownloadRequest): APIResponse<Blob> =>
      HttpClient.download(`${DOCUMENT_SERVICE}/document/file/s3/download`, payload, {}, false, false),

    downloadDocumentpost: (payload: IDocumentDownloadRequest, config: AxiosRequestConfig): APIResponse<Blob> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/file/s3/download`, payload, config),

    downloadDocumentBlob: (payload: IDocumentDownloadRequest): APIResponse<Blob> =>
      HttpClient.download(`${DOCUMENT_SERVICE}/document/file/s3/download`, payload, {}, false, true),

    getDocumentPresignUrl: (payload: IDocumentDownloadRequest): APIResponse<IDocumentPresignResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/file/s3/presign`, payload),

    searchDocument: (payload: IDocumentSearchRequest, config: AxiosRequestConfig): APIResponse<IDocumentSearchResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/search`, payload, config),

    getDocumentTypes: (payload: IGetDocumentTypesRequest): APIResponse<IGetDocumentTypesResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/view/doctypeslist`, payload),

    viewDocumentCategories: (payload: { scope: string; clientID?: string }): APIResponse<IDocumentCategoryViewResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/category/view`, payload),

    saveDocumentCategory: (payload: any): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/category/save`, payload),

    removeDocumentCategory: (payload: { id: string }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/category/remove`, payload),

    // New: Fetch folder template tree
    getFolderTemplateTree: (): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/folder/template/view`, {}),

    getConfigFields: (payload: { advisorId: string }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/configuration/view`, payload),

    createConfigFields: (payload: { advisorId: string }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/configuration/create`, payload),

    updateConfigFields: (payload: {
      advisorID: string;
      fields: {
        documentName: boolean;
        version: boolean;
        category: boolean;
        subCategory: boolean;
        author: boolean;
        status: boolean;
        lastUpdated: boolean;
        clientName: boolean;
        folder: boolean;
        aiProcess: boolean;
      };
    }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/configuration/update`, payload),

    // New: Save folder template tree
    saveFolderTemplateTree: (payload: any): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/folder/template/edit`, payload),

    moveDocumentToTrash: (payload: { documentIDs: string[]; advisorID: string; comment: string }): Promise<any> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/trash/documents`, payload),

    editDocumentDetails: (payload: IDocumentEditRequest): APIResponse<IEmptyRequestResponse> => HttpClient.post(`${DOCUMENT_SERVICE}/document/edit`, payload),

    // Fetch special actions for quick actions
    getSpecialActions: (payload: { scope: string; clientID?: string }): Promise<any> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/specialaction/view`, payload),

    // Save special action (quick action)
    saveSpecialAction: (payload: any): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/specialaction/save`, payload),

    // Fetch folder node tree for new browse component
    getDocumentNodeTree: (payload: { clientID: string }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/node/view`, payload),

    // Save client-specific folder node tree
    saveDocumentNodeTree: (payload: any): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/node/save`, payload),

    // Create new folder node
    createDocumentNode: (payload: { parentID: string; name: string; isEditable: boolean }): Promise<any> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/node/create`, payload),

    // Edit/move folder node
    editDocumentNode: (payload: { action: string; id: string; name: string }): Promise<any> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/node/edit`, payload),

    // Delete folder node
    deleteDocumentNode: (payload: { id: string }): Promise<any> => HttpClient.post(`${DOCUMENT_SERVICE}/document/node/delete`, payload),

    // New: Upload client document
    createClientDocument: (
      payload: IDocumentUploadRequest,
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
    ): APIResponse<IDocumentUploadResponse> => HttpClient.upload(`${DOCUMENT_SERVICE}/document/client/file/s3/upload`, payload, { onUploadProgress }),

    viewDocumentStatus: (): APIResponse<IDocumentStatusViewResponse> => HttpClient.post(`${DOCUMENT_SERVICE}/document/status/view`, {}),

    getDeletedDocuments: (payload: {
      clientID: string;
      pageSize: number;
      pageIndex: number;
      skipPagination: boolean;
      expireFilter: string;
    }): APIResponse<IGetDeletedDocumentsResponse> => HttpClient.post(`${DOCUMENT_SERVICE}/document/trash/documents/view`, payload),

    restoreDocuments: (payload: { documentIDs: string[] }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/trash/documents/restore`, payload),

    deleteDocumentsPermanently: (payload: { documentIDs?: string[]; clientID?: string; comment?: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${DOCUMENT_SERVICE}/document/delete/permanent`, payload),
  };
};
